/**
 * Step 2's marks: which words and phrases become cards, gaps or both (handoff §4,
 * ADR-115 Decisions 3, 7 and 8).
 *
 * Two identities meet here, and they are kept apart:
 * - **A card is keyed as the server de-duplicates it** (`text_cards.plan_card_words`):
 *   a one-lemma word by its lemma, a compound such as "well-known" by its text. So
 *   «offices» and «office» are one card, and a word picked in the list is the same
 *   mark as the word tapped in the text.
 * - **A gap is a form,** and its occurrences are never computed here (ADR-115
 *   Decision 4): they come from `POST /texts/flow/gaps`, the server's own matcher.
 *   Until that answers, only the word the learner chose shows as a gap.
 *
 * A phrase is a run of words the learner selected, carrying one role. Its card is
 * the phrase as written; its gap is the phrase's form, wherever the server finds it.
 * No selection may overlap another phrase or a gap occurrence (Decision 3).
 */
import type { AnalysedWord, CardMark, TextAnalysis, WordCandidateResponse } from '../types'
import { initialMarked, lemmaKey, lemmaSpans } from './textFlow'

export type Role = 'card' | 'gap' | 'both'
export type Span = [number, number]

export interface WordGap {
  /** The form sent to the server: the word as written. */
  form: string
  /** The word the learner chose: shown as a gap before the server answers. */
  word: number
}

export interface Phrase {
  id: string
  first: number
  last: number
  role: Role
}

export interface Marks {
  /** Card keys of single words (see `wordKey`). */
  cards: string[]
  /** Keys the learner touched: never app-suggested any more (for «Снять»). */
  manual: string[]
  wordGaps: WordGap[]
  phrases: Phrase[]
}

export const EMPTY_MARKS: Marks = { cards: [], manual: [], wordGaps: [], phrases: [] }

export const hasCard = (role: Role) => role !== 'gap'
export const hasGap = (role: Role) => role !== 'card'

// ── Card keys ─────────────────────────────────────────────────────────────────

/** A word's card identity, or null for a word with no lemma ("A1"): it can only be a gap. */
export function wordKey(w: AnalysedWord): string | null {
  if (w.lemmas.length === 1) return `l:${w.lemmas[0]}`
  if (w.lemmas.length > 1) return `w:${w.text.toLowerCase()}`
  return null
}

/** The card key of a list chip (a lemma): the compound itself when the lemma is met only inside one. */
export function listKey(analysis: TextAnalysis, lemma: string): string {
  return lemmaKey(lemmaSpans(analysis), lemma)
}

/** The server's pre-selection, as card keys. */
export function suggestedKeys(analysis: TextAnalysis): string[] {
  return [...new Set(initialMarked(analysis).map((l) => listKey(analysis, l)))]
}

export function initialMarks(analysis: TextAnalysis): Marks {
  return { ...EMPTY_MARKS, cards: suggestedKeys(analysis) }
}

/** What the analysis knows about a one-lemma word: its CEFR level and translation. */
export function wordItem(analysis: TextAnalysis, w: AnalysedWord): WordCandidateResponse | null {
  if (w.lemmas.length !== 1) return null
  const lemma = w.lemmas[0]
  const g = analysis.groups
  for (const list of [analysis.candidates, g.already_studying, g.already_known, g.function_words, g.names]) {
    const hit = list.find((i) => i.lemma === lemma)
    if (hit) return hit
  }
  return null
}

// ── Phrases ───────────────────────────────────────────────────────────────────

/** A phrase as written in the text, its whitespace runs collapsed (the server's form normalisation). */
export function phraseText(body: string, words: AnalysedWord[], p: { first: number; last: number }): string {
  return body.slice(words[p.first].start, words[p.last].end).replace(/\s+/g, ' ')
}

/** The forms the save sends, each once, in reading order of the word or phrase they came from. */
export function submittedForms(body: string, words: AnalysedWord[], marks: Marks): string[] {
  const items: [number, string][] = [
    ...marks.wordGaps.map((g): [number, string] => [words[g.word].start, g.form]),
    ...marks.phrases.filter((p) => hasGap(p.role)).map((p): [number, string] => [words[p.first].start, phraseText(body, words, p)]),
  ]
  items.sort((a, b) => a[0] - b[0])
  return [...new Set(items.map(([, f]) => f))]
}

// ── Gap occurrences (the server's), mapped onto words ─────────────────────────

/** The latest answer of `POST /texts/flow/gaps`: occurrences and the stored form, per submitted form. */
export interface GapMatch {
  /** The forms this answer was for: overlaps are resolved over the whole set. */
  forms: string[]
  occurrences: Record<string, Span[]>
  resolved: Record<string, string | null>
}

/**
 * Each submitted form's occurrences as the view shows them: the server's when it
 * has answered for that form, otherwise only the word or phrase the learner chose.
 */
export function shownOccurrences(
  body: string,
  words: AnalysedWord[],
  marks: Marks,
  match: GapMatch | null,
): Map<string, Span[]> {
  const shown = new Map<string, Span[]>()
  for (const form of submittedForms(body, words, marks)) {
    const known = match?.occurrences[form]
    if (known) shown.set(form, known)
  }
  const optimistic = (form: string, span: Span) => {
    if (!shown.has(form)) shown.set(form, [span])
  }
  for (const g of marks.wordGaps) optimistic(g.form, [words[g.word].start, words[g.word].end])
  for (const p of marks.phrases) {
    if (hasGap(p.role)) optimistic(phraseText(body, words, p), [words[p.first].start, words[p.last].end])
  }
  return shown
}

/** The submitted forms the server found no occurrence for: the save would refuse them. */
export function lostForms(body: string, words: AnalysedWord[], marks: Marks, match: GapMatch | null): string[] {
  const forms = submittedForms(body, words, marks)
  // Only an answer for exactly these forms says what the save would refuse.
  if (!match || JSON.stringify(match.forms) !== JSON.stringify(forms)) return []
  return forms.filter((f) => match.occurrences[f]?.length === 0)
}

/** First and last word index of a span (spans always run from a word's start to a word's end). */
function wordRange(words: AnalysedWord[], [start, end]: Span): [number, number] | null {
  let first = -1
  let last = -1
  for (let i = 0; i < words.length; i++) {
    if (first === -1 && words[i].start >= start) first = i
    if (words[i].end <= end) last = i
    if (words[i].start >= end) break
  }
  return first === -1 || last < first ? null : [first, last]
}

export interface GapCover {
  /** For each word: the submitted form whose occurrence covers it, or null. */
  owner: (string | null)[]
  /** For each word: the word range of the occurrence covering it, or null. */
  range: ([number, number] | null)[]
}

export function gapCover(words: AnalysedWord[], shown: Map<string, Span[]>): GapCover {
  const owner: (string | null)[] = words.map(() => null)
  const range: ([number, number] | null)[] = words.map(() => null)
  for (const [form, spans] of shown) {
    for (const s of spans) {
      const r = wordRange(words, s)
      if (!r) continue
      for (let i = r[0]; i <= r[1]; i++) {
        if (owner[i] !== null) continue
        owner[i] = form
        range[i] = r
      }
    }
  }
  return { owner, range }
}

// ── What each word looks like ─────────────────────────────────────────────────

export interface WordLook {
  card: boolean
  gap: boolean
  /** The phrase the word is in, and where in it. */
  phrase: string | null
  edge: 'only' | 'first' | 'mid' | 'last' | null
  /** Edit mode: the receiving deck already holds this word's card (ADR-115 Amendment 3). */
  carded?: boolean
}

/** The text between word i and word i+1: filled inside a card phrase, underlined inside one gap occurrence. */
export interface SepLook {
  fill: boolean
  underline: boolean
}

export function phraseAt(marks: Marks, i: number): Phrase | null {
  return marks.phrases.find((p) => i >= p.first && i <= p.last) ?? null
}

export function looks(
  words: AnalysedWord[],
  marks: Marks,
  cover: GapCover,
): { words: WordLook[]; seps: SepLook[] } {
  const cards = new Set(marks.cards)
  const ws: WordLook[] = words.map((w, i) => {
    const p = phraseAt(marks, i)
    if (p) {
      const edge = p.first === p.last ? 'only' : i === p.first ? 'first' : i === p.last ? 'last' : 'mid'
      return { card: hasCard(p.role), gap: cover.owner[i] !== null, phrase: p.id, edge }
    }
    const key = wordKey(w)
    return { card: key !== null && cards.has(key), gap: cover.owner[i] !== null, phrase: null, edge: null }
  })
  const seps: SepLook[] = words.slice(0, -1).map((_, i) => {
    const p = ws[i].phrase
    const r = cover.range[i]
    return {
      fill: p !== null && p === ws[i + 1].phrase && ws[i].card,
      underline: r !== null && r === cover.range[i + 1] && i + 1 <= r[1],
    }
  })
  return { words: ws, seps }
}

// ── Transitions (pure: each returns new marks) ────────────────────────────────

const withManual = (marks: Marks, key: string | null): string[] =>
  key === null || marks.manual.includes(key) ? marks.manual : [...marks.manual, key]

/** The role a single word has now, or null when it is plain. */
export function wordRole(words: AnalysedWord[], marks: Marks, cover: GapCover, i: number): Role | null {
  const key = wordKey(words[i])
  const card = key !== null && marks.cards.includes(key)
  const gap = cover.owner[i] !== null
  return card && gap ? 'both' : card ? 'card' : gap ? 'gap' : null
}

/** Removes the word's card and every word gap whose occurrence covers it (a gap is its form, everywhere). */
export function removeWord(words: AnalysedWord[], marks: Marks, cover: GapCover, i: number): Marks {
  const key = wordKey(words[i])
  const covering = cover.owner[i]
  return {
    ...marks,
    cards: key === null ? marks.cards : marks.cards.filter((k) => k !== key),
    manual: withManual(marks, key),
    wordGaps: marks.wordGaps.filter((g) => g.word !== i && g.form !== covering),
  }
}

export function setWordRole(words: AnalysedWord[], marks: Marks, cover: GapCover, i: number, role: Role): Marks {
  const key = wordKey(words[i])
  // A word with no lemma cannot be a card: whatever is asked, it is a gap.
  const effective: Role = key === null ? 'gap' : role
  const cleared = removeWord(words, marks, cover, i)
  const isGap = cover.owner[i] !== null
  return {
    ...cleared,
    cards: hasCard(effective) && key !== null ? [...cleared.cards, key] : cleared.cards,
    // An existing gap keeps its form (and so its occurrences); a new one is the word as written.
    wordGaps: !hasGap(effective)
      ? cleared.wordGaps
      : isGap
        ? marks.wordGaps
        : [...marks.wordGaps, { form: words[i].text, word: i }],
  }
}

/** The words a new or resized phrase may not cross (ADR-115 Decision 3). */
export function blockedWords(
  words: AnalysedWord[],
  marks: Marks,
  cover: GapCover,
  opts: { anchor?: number; phrase?: string } = {},
): boolean[] {
  // The anchor's own word gap goes when the phrase replaces it, so its occurrences do not block.
  const anchorForm = opts.anchor !== undefined ? cover.owner[opts.anchor] : null
  const ownPhrase = opts.phrase ? marks.phrases.find((p) => p.id === opts.phrase) : null
  const ownForm = ownPhrase && hasGap(ownPhrase.role) ? cover.owner[ownPhrase.first] : null
  return words.map((_, i) => {
    const p = phraseAt(marks, i)
    if (p && p.id !== opts.phrase) return true
    const form = cover.owner[i]
    if (form === null) return false
    if (anchorForm !== null && form === anchorForm && isWordGapForm(marks, form)) return false
    return form !== ownForm
  })
}

/** True when the form was chosen on a single word (not a phrase). */
export const isWordGapForm = (marks: Marks, form: string) => marks.wordGaps.some((g) => g.form === form)

/** From `anchor` toward `target`, stopping before the first blocked word. */
export function clampRange(anchor: number, target: number, blocked: boolean[]): [number, number] {
  const step = target >= anchor ? 1 : -1
  let reach = anchor
  for (let i = anchor + step; step > 0 ? i <= target : i >= target; i += step) {
    if (blocked[i]) break
    reach = i
  }
  return reach >= anchor ? [anchor, reach] : [reach, anchor]
}

let nextPhraseId = 0
export const newPhraseId = () => `p${++nextPhraseId}`

/**
 * A new phrase over words first..last, role Card unless the caller says otherwise (edit
 * mode marks gaps only, so it passes 'gap': a Card phrase there is highlighted but
 * never reaches the text). It replaces the single-word marks inside its range (§4.3);
 * the caller has already clamped the range so it overlaps no phrase and no gap occurrence.
 */
export function makePhrase(
  words: AnalysedWord[],
  marks: Marks,
  cover: GapCover,
  first: number,
  last: number,
  id: string = newPhraseId(),
  role: Role = 'card',
): Marks {
  let next = marks
  for (let i = first; i <= last; i++) next = removeWord(words, next, cover, i)
  return { ...next, phrases: [...next.phrases, { id, first, last, role }] }
}

export function resizePhrase(marks: Marks, id: string, first: number, last: number): Marks {
  return { ...marks, phrases: marks.phrases.map((p) => (p.id === id ? { ...p, first, last } : p)) }
}

export function setPhraseRole(marks: Marks, id: string, role: Role): Marks {
  return { ...marks, phrases: marks.phrases.map((p) => (p.id === id ? { ...p, role } : p)) }
}

export function removePhrase(marks: Marks, id: string): Marks {
  return { ...marks, phrases: marks.phrases.filter((p) => p.id !== id) }
}

// ── «Снять» / «Предложить слова» ───────────────────────────────────────────────

/** Suggested words still marked only because the app suggested them. */
export function activeSuggestions(analysis: TextAnalysis, marks: Marks): string[] {
  return suggestedKeys(analysis).filter((k) => marks.cards.includes(k) && !marks.manual.includes(k))
}

/** «Снять»: removes app-suggested marks only; manual marks and phrases stay. */
export function dropSuggestions(analysis: TextAnalysis, marks: Marks): Marks {
  const drop = new Set(activeSuggestions(analysis, marks))
  return { ...marks, cards: marks.cards.filter((k) => !drop.has(k)) }
}

/**
 * The suggestions «Предложить слова» would add: unmarked, not a gap, not inside a phrase,
 * and not a card the receiving deck already holds (*held*, edit mode only).
 */
export function addableSuggestions(
  analysis: TextAnalysis,
  marks: Marks,
  cover: GapCover,
  held: ReadonlySet<string> = new Set(),
): string[] {
  const words = analysis.words
  return suggestedKeys(analysis).filter((key) => {
    if (marks.cards.includes(key) || held.has(key)) return false
    const occurrences = words.map((w, i) => [w, i] as const).filter(([w]) => wordKey(w) === key)
    return occurrences.some(([, i]) => cover.owner[i] === null && !phraseAt(marks, i))
  })
}

export function addSuggestions(
  analysis: TextAnalysis,
  marks: Marks,
  cover: GapCover,
  held: ReadonlySet<string> = new Set(),
): Marks {
  const add = addableSuggestions(analysis, marks, cover, held)
  return { ...marks, cards: [...marks.cards, ...add], manual: marks.manual.filter((k) => !add.includes(k)) }
}

// ── The list view: a chip toggles mark / unmark only (§5) ─────────────────────

export function listMark(marks: Marks, key: string): Marks {
  if (marks.cards.includes(key)) return marks
  return { ...marks, cards: [...marks.cards, key], manual: withManual(marks, key) }
}

/** Unmarking a chip in «Отмеченные» removes its card and the gaps of its words. */
export function listUnmark(words: AnalysedWord[], marks: Marks, key: string): Marks {
  return {
    ...marks,
    cards: marks.cards.filter((k) => k !== key),
    manual: withManual(marks, key),
    wordGaps: marks.wordGaps.filter((g) => wordKey(words[g.word]) !== key),
  }
}

/** A chip's role for its suffix: from the word's card and the word gaps chosen on its words. */
export function keyRole(words: AnalysedWord[], marks: Marks, key: string): Role | null {
  const card = marks.cards.includes(key)
  const gap = marks.wordGaps.some((g) => wordKey(words[g.word]) === key)
  return card && gap ? 'both' : card ? 'card' : gap ? 'gap' : null
}

// ── Counts and the save payload ───────────────────────────────────────────────

/** The card marks the save sends: one span per single-word card, one per card phrase. */
export function saveCardMarks(analysis: TextAnalysis, marks: Marks): CardMark[] {
  const { words } = analysis
  const out: CardMark[] = []
  for (const key of new Set(marks.cards)) {
    const w = words.find((x) => wordKey(x) === key)
    if (w) out.push({ start: w.start, end: w.end })
  }
  for (const p of marks.phrases) {
    if (hasCard(p.role)) out.push({ start: words[p.first].start, end: words[p.last].end })
  }
  return out.sort((a, b) => a.start - b.start)
}

/** Cards the marks make, as the server de-duplicates them. */
export function cardCount(body: string, analysis: TextAnalysis, marks: Marks): number {
  const { words } = analysis
  const phrases = new Set(
    marks.phrases.filter((p) => hasCard(p.role)).map((p) => phraseText(body, words, p).toLowerCase()),
  )
  const single = new Set(marks.cards.filter((k) => words.some((w) => wordKey(w) === k)))
  return single.size + phrases.size
}

/** The forms the save sends: every submitted form except those the server found nowhere. */
export function saveForms(body: string, words: AnalysedWord[], marks: Marks, match: GapMatch | null): string[] {
  const lost = new Set(lostForms(body, words, marks, match))
  return submittedForms(body, words, marks).filter((f) => !lost.has(f))
}

/** Gaps the save makes: distinct stored forms once the server has answered, else each submitted form. */
export function gapCount(body: string, words: AnalysedWord[], marks: Marks, match: GapMatch | null): number {
  const forms = saveForms(body, words, marks, match)
  return new Set(forms.map((f) => match?.resolved[f] ?? f)).size
}

// ── «Роль для всех»: one role for every mark ───────────────────────────────────

/** Marks whose role cannot be a card, however it is asked (ADR-115 Amendment 3 Decision 5). */
export interface BulkLimits {
  /** A word the receiving deck already holds: gap only. */
  cardlessWord?: (i: number) => boolean
  cardlessPhrase?: (p: Phrase) => boolean
}

/**
 * The marked single words, each once: a card key by its first occurrence outside a
 * phrase, and every word gap by the word the learner chose. A mark never reached by a
 * word of the analysis, or only inside phrases, is left out.
 */
function markedWords(words: AnalysedWord[], marks: Marks): number[] {
  const inPhrase = new Set<number>()
  for (const p of marks.phrases) for (let i = p.first; i <= p.last; i++) inPhrase.add(i)
  const firstOf = new Map<string, number>()
  words.forEach((w, i) => {
    const key = wordKey(w)
    if (key !== null && !inPhrase.has(i) && !firstOf.has(key)) firstOf.set(key, i)
  })
  const out = new Set<number>()
  for (const key of marks.cards) {
    const i = firstOf.get(key)
    if (i !== undefined) out.add(i)
  }
  for (const g of marks.wordGaps) if (!inPhrase.has(g.word)) out.add(g.word)
  return [...out].sort((a, b) => a - b)
}

/** True when there is a word or phrase mark for the control to act on. */
export const hasRoleTargets = (words: AnalysedWord[], marks: Marks) =>
  marks.phrases.length > 0 || markedWords(words, marks).length > 0

/** The role every mark has, or null when they differ (or there is none). */
export function commonRole(words: AnalysedWord[], marks: Marks, cover: GapCover): Role | null {
  const roles = [
    ...markedWords(words, marks).map((i) => wordRole(words, marks, cover, i)),
    ...marks.phrases.map((p) => p.role),
  ]
  return roles.length > 0 && roles.every((r) => r === roles[0]) ? roles[0] : null
}

/**
 * Every marked word and phrase takes *role*. A mark that cannot be a card (a word with
 * no lemma, or one the receiving deck holds) keeps what it has when a card role is
 * asked. A mark that already has the role is not touched, so a suggested word stays
 * «suggested». Returns the same marks object when nothing changes.
 */
export function setAllRoles(
  words: AnalysedWord[],
  marks: Marks,
  cover: GapCover,
  role: Role,
  limits: BulkLimits = {},
): Marks {
  let next = marks
  for (const i of markedWords(words, marks)) {
    if (wordRole(words, marks, cover, i) === role) continue
    if (hasCard(role) && (wordKey(words[i]) === null || limits.cardlessWord?.(i))) continue
    next = setWordRole(words, next, cover, i, role)
  }
  const phrases = next.phrases.map((p) =>
    p.role === role || (hasCard(role) && limits.cardlessPhrase?.(p)) ? p : { ...p, role },
  )
  return phrases.every((p, n) => p === next.phrases[n]) ? next : { ...next, phrases }
}
